/**
 * Shared readiness helpers for Lens Intelligence catalog / Ask Lens badges.
 *
 * Important: only use indexing_status / pipeline_status — never IRL `status`
 * (that is a workflow field like Pending/Completed).
 */

export function isIntelligenceSourceProcessing(item) {
    const raw = String(item?.indexing_status || item?.pipeline_status || '')
        .toLowerCase()
        .trim();
    if (
        !raw ||
        raw === 'ready' ||
        raw === 'indexed' ||
        raw === 'complete' ||
        raw === 'completed' ||
        raw === 'not_applicable' ||
        raw === 'not_found'
    ) {
        return false;
    }
    return (
        raw === 'processing' ||
        raw === 'pending' ||
        raw === 'queued' ||
        raw === 'uploading' ||
        raw.includes('process') ||
        raw.includes('pending') ||
        raw.includes('upload')
    );
}

function documentId(doc) {
    const id = Number(doc?.id ?? doc?.file_id ?? doc?.document_id);
    return Number.isFinite(id) ? id : null;
}

/**
 * Count ready vs in-flight sources.
 *
 * Ready = RAG-ready catalog docs (+ IRL / observations).
 * Processing = local VDR indexing queue only (same lifecycle as the VDR upload
 * tray). Catalog `indexing_status=processing` (keyword done, RAG catching up)
 * is intentionally not counted here so Ask Lens matches VDR's "still indexing"
 * count instead of double-counting.
 */
export function countIntelligenceSourceReadiness({
    documents = [],
    irls = [],
    observations = [],
    indexingFileIds = [],
} = {}) {
    const indexingSet = new Set(
        (indexingFileIds || []).map(Number).filter(Number.isFinite),
    );

    let vdrReady = 0;
    const catalogIds = new Set();
    for (const doc of documents || []) {
        const id = documentId(doc);
        if (id != null) catalogIds.add(id);
        const indexingStatus = String(doc?.indexing_status || '')
            .toLowerCase()
            .trim();
        // Only RAG-ready (or legacy rows without status) count as ready sources.
        if (
            !indexingStatus ||
            indexingStatus === 'ready' ||
            indexingStatus === 'indexed'
        ) {
            if (!isIntelligenceSourceProcessing(doc)) {
                vdrReady += 1;
            }
        }
    }

    // In-flight uploads not yet cleared from the VDR indexing queue.
    let vdrProcessing = 0;
    for (const id of indexingSet) {
        vdrProcessing += 1;
    }

    const irlReady = (irls || []).length;
    const obsReady = (observations || []).length;

    return {
        vdr: {
            ready: vdrReady,
            processing: vdrProcessing,
            total: vdrReady + vdrProcessing,
        },
        irl: { ready: irlReady, processing: 0, total: irlReady },
        obs: { ready: obsReady, processing: 0, total: obsReady },
        readyTotal: vdrReady + irlReady + obsReady,
        processingTotal: vdrProcessing,
    };
}

function inferSourceReadinessType(source = {}) {
    const explicit = String(source.sourceType || source.source_type || '').toLowerCase();
    if (
        explicit === 'vdr' ||
        explicit === 'irl' ||
        explicit === 'observation' ||
        explicit === 'observations'
    ) {
        return explicit === 'observations' ? 'observation' : explicit;
    }
    const meta = String(source.meta || '').toLowerCase();
    if (meta.startsWith('vdr')) return 'vdr';
    if (meta === 'irl' || meta.startsWith('irl')) return 'irl';
    if (meta === 'observation' || meta.startsWith('observation')) return 'observation';
    return null;
}

/** Route to open the source in its native module side panel. */
export function resolveSourceReadinessRoute(source = {}) {
    const target = buildSourceReadinessNavigation(source);
    return target?.path || null;
}

/**
 * Build navigation target for a readiness / snapshot catalog row.
 * Uses router state for VDR when workstream (and optional folder) are known
 * so Dataroom opens the exact file viewer.
 */
export function buildSourceReadinessNavigation(source = {}) {
    const id = source.id;
    if (id == null || id === '') return null;

    const type = inferSourceReadinessType(source);
    const encodedId = encodeURIComponent(String(id));

    if (type === 'irl') {
        return { path: `/irl?irlId=${encodedId}` };
    }
    if (type === 'observation') {
        return { path: `/observations?observationId=${encodedId}` };
    }
    if (type === 'vdr') {
        const wsId = source.workstreamId ?? source.workstream_id;
        const folderId = source.wsFolderId ?? source.ws_folder_id;
        if (wsId != null && wsId !== '') {
            return {
                path: '/dataroom',
                state: {
                    fromSharedLink: true,
                    workstreamId: Number(wsId) || wsId,
                    folderId: folderId != null && folderId !== '' ? Number(folderId) || folderId : undefined,
                    document: {
                        id: Number(id) || id,
                        document_name: source.title || undefined,
                        ws_folder: source.meta?.startsWith('VDR · ')
                            ? source.meta.slice('VDR · '.length)
                            : undefined,
                    },
                },
            };
        }
        return { path: `/dataroom?fileId=${encodedId}` };
    }

    return { path: `/dataroom?fileId=${encodedId}` };
}

function unavailableSourceId(source = {}) {
    return source.source_id || source.sourceId || source.id || null;
}

function normalizeSourceReadinessInput(sourceReadiness = {}) {
    if (
        sourceReadiness.unavailableSources ||
        sourceReadiness.failedSourceId != null ||
        sourceReadiness.failedSourceLabel
    ) {
        return sourceReadiness;
    }

    const summary = sourceReadiness.summary || {};
    const sources = sourceReadiness.sources || [];
    const failedSources = sources.filter(
        source => String(source.status || '').toLowerCase() === 'failed',
    );
    const materialSources = sources.filter(source => Boolean(source.impact));

    return {
        ready: summary.ready,
        processing: summary.processing,
        failed: summary.failed,
        failedSourceId: failedSources[0]?.id || null,
        unavailableSources: [
            ...failedSources.map(source => ({
                source_id: source.id,
                label: source.title,
                reason: 'failed',
            })),
            ...materialSources
                .filter(source => !failedSources.some(f => f.id === source.id))
                .map(source => ({
                    source_id: source.id,
                    label: source.title,
                    reason: 'unavailable',
                    impact: source.impact,
                })),
        ],
    };
}

/**
 * Focus payload for Overview → Source Readiness drawer from module review actions.
 */
export function buildSourceReadinessReviewFocus(sourceReadiness = {}) {
    const readiness = normalizeSourceReadinessInput(sourceReadiness);
    const unavailable = readiness.unavailableSources || [];
    const failedSources = unavailable.filter(
        source => String(source?.reason || '').toLowerCase() === 'failed',
    );
    const failedCount = Number(readiness.failed ?? 0);

    if (readiness.failedSourceId || failedCount > 0 || failedSources.length) {
        return {
            tab: 'Failed',
            sourceId:
                readiness.failedSourceId ||
                unavailableSourceId(failedSources[0]) ||
                null,
        };
    }

    const affected = unavailable.filter(
        source =>
            source?.material ||
            source?.impact ||
            String(source?.reason || '').toLowerCase() === 'unavailable',
    );
    if (affected.length) {
        return {
            tab: 'Material impact',
            sourceId: unavailableSourceId(affected[0]) || null,
        };
    }

    if (Number(readiness.processing ?? 0) > 0) {
        return { tab: 'Processing', sourceId: null };
    }

    return { tab: 'All', sourceId: null };
}
